import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Ícones SVG para cada tipo de toast
const icons = {
  success: (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
      <polyline points="22 4 12 14.01 9 11.01"></polyline>
    </svg>
  ),
  error: (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="15" y1="9" x2="9" y2="15"></line>
      <line x1="9" y1="9" x2="15" y2="15"></line>
    </svg>
  ),
  warning: (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
      <line x1="12" y1="9" x2="12" y2="13"></line>
      <line x1="12" y1="17" x2="12.01" y2="17"></line>
    </svg>
  )
};

// Animação de entrada
const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

// Animação de saída
const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

// Estilização do container principal dos toasts
const ToastContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Estilização individual de cada toast
const ToastElement = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background: white;
  color: #333;
  min-width: 300px;
  max-width: 400px;
  animation: ${slideIn} 0.3s ease forwards;
  
  &.exiting {
    animation: ${slideOut} 0.3s ease forwards;
  }

  // Cores baseadas no tipo
  ${props => props.type === 'success' && `
    border-left: 4px solid #4CAF50;
    svg { color: #4CAF50; }
  `}
  
  ${props => props.type === 'error' && `
    border-left: 4px solid #F44336;
    svg { color: #F44336; }
  `}
  
  ${props => props.type === 'warning' && `
    border-left: 4px solid #FF9800;
    svg { color: #FF9800; }
  `}
`;

// Estilização do ícone
const IconWrapper = styled.div`
  margin-right: 12px;
  display: flex;
  align-items: center;
`;

// Estilização do conteúdo textual
const Content = styled.div`
  flex: 1;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  font-size: 14px;
  line-height: 1.4;
`;

// Estilização do botão de fechar
const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #999;
  margin-left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #666;
  }
`;

// Componente individual do Toast
const Toast = ({ type, message, onClose, duration = 10000 }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <ToastElement type={type}>
      <IconWrapper>
        {icons[type]}
      </IconWrapper>
      <Content>{message}</Content>
      <CloseButton onClick={onClose}>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </CloseButton>
    </ToastElement>
  );
};

// Componente ToastManager modificado para receber props
export const ToastManager = ({ toasts, removeToast }) => {
    return (
      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            type={toast.type}
            message={toast.message}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    );
  };
  
  // Export apenas o componente Toast se precisar usar individualmente
  export { Toast };